import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import { inyectarLogoEnExcel } from "./ExcelLogo";
import {
  headerStyle, totalTextoStyle, totalMoneyStyle,
  celdaMoneda, celdaTexto,
  direccionEmpresa, contactoEmpresa, nombreEmpresa,
} from "./ExcelEstilos";

/**
 * Excel del resumen de Cuentas por Cobrar: el equivalente del PDF de Totales
 * (`Pdf/reporteDeCuentasPorCobrarTotales.js`), con el mismo encabezado de
 * empresa y su logo.
 *
 * Consume el mismo `values` que el PDF: la respuesta cruda de
 * `reportesAdministrativos/reporteCuentasPorCobrar`, que las dos páginas
 * guardan en `state.dataReporte`.
 *
 * Diferencia deliberada con el PDF: aquí las ventas se separan por moneda antes
 * de sumarse. El PDF acumula `saldo` en crudo, así que con facturas en dólares y
 * en pesos su gran total mezcla unidades; el PDF principal
 * (`reporteDeCuentasPorCobrar.js`) sí genera una tabla por moneda y ese es el
 * criterio que se sigue aquí. Con una sola moneda —el caso normal— los números
 * salen idénticos a los del PDF de Totales.
 */

// A: Cliente · B: Total · C: Total Vencido · D: Total No Vencido
const COLUMNAS = [
  { titulo: "Cliente", ancho: 45 },
  { titulo: "Total", ancho: 18 },
  { titulo: "Total Vencido", ancho: 18 },
  { titulo: "Total No Vencido", ancho: 18 },
];
const COL_ULTIMA = COLUMNAS.length - 1;

// Renglones que ocupa el encabezado de la empresa. El logo se ancla en A1 y se
// encaja en una caja de 200x75 px, así que estos renglones tienen que sumar más
// alto que eso o Excel lo recorta.
const FILAS_ENCABEZADO = 4;
const ALTO_ENCABEZADO = 16;

// El encabezado no lleva la retícula de la tabla: sus estilos van sin `border`.
const tituloEmpresaStyle = { font: { bold: true, sz: 14 }, alignment: { vertical: "center" } };
const datosEmpresaStyle = { font: { sz: 10 }, alignment: { vertical: "center" } };
const metaStyle = { font: { sz: 9, color: { rgb: "8C8C8C" } }, alignment: { vertical: "center" } };
const tituloReporteStyle = {
  font: { bold: true, sz: 12, color: { rgb: "005F6B" } },
  alignment: { horizontal: "center", vertical: "center" },
};
const subtituloMonedaStyle = { font: { bold: true, sz: 11 }, alignment: { vertical: "center" } };
const totalDestacadoTextoStyle = { ...totalTextoStyle, fill: { fgColor: { rgb: "E8E8E8" } } };
const totalDestacadoMoneyStyle = { ...totalMoneyStyle, fill: { fgColor: { rgb: "E8E8E8" } } };

const vacia = () => ({ v: "" });
const filaVacia = () => COLUMNAS.map(() => vacia());

const esFechaValida = (valor) =>
  typeof valor === "string" && moment(valor, "DD/MM/YYYY", true).isValid();

/**
 * Las filas hijas de insumo (las que agrega `desglosarProductos`) llegan sin
 * folio y con la *cantidad* metida en `fechaFactura`, así que se reconocen —y se
 * descartan— con el mismo guard que usa el PDF de Totales.
 */
const esPrincipal = (venta) => Boolean(venta.folio) && esFechaValida(venta.fechaFactura);

/**
 * Agrupa en `moneda -> cliente -> { total, vencido, noVencido }`.
 *
 * Una factura sin fecha de vencimiento válida (el backend manda "-") cuenta como
 * no vencida, igual que en el PDF.
 */
const agruparPorMonedaYCliente = (ventas) => {
  const porMoneda = {};

  for (const venta of ventas || []) {
    if (!esPrincipal(venta)) continue;

    const moneda = venta.moneda || "Sin moneda";
    const cliente = venta.cliente || "Sin cliente";
    if (!porMoneda[moneda]) porMoneda[moneda] = {};
    if (!porMoneda[moneda][cliente]) {
      porMoneda[moneda][cliente] = { total: 0, vencido: 0, noVencido: 0 };
    }

    const saldo = venta.saldo || 0;
    const totales = porMoneda[moneda][cliente];
    totales.total += saldo;

    const vencida = esFechaValida(venta.fechaVencimiento)
      && moment(venta.fechaVencimiento, "DD/MM/YYYY").isBefore(moment());
    if (vencida) totales.vencido += saldo;
    else totales.noVencido += saldo;
  }

  return porMoneda;
};

/**
 * @param {Object} values  `state.dataReporte`: la respuesta de
 *                         `reportesAdministrativos/reporteCuentasPorCobrar`
 * @returns {Promise<Blob>} el .xlsx listo para descargar
 */
const construirExcelCuentasPorCobrarTotales = async (values) => {
  const empresa = values?.empresa || {};
  const porMoneda = agruparPorMonedaYCliente(values?.ventas);

  const filas = [];
  const merges = [];

  // Une B:D del renglón que se acaba de empujar (a la derecha del logo).
  const unirDerecha = () => {
    merges.push({ s: { r: filas.length - 1, c: 1 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
  };
  // Une el renglón completo, A:D.
  const unirTodo = () => {
    merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
  };
  const empujarDerecha = (texto, estilo) => {
    filas.push([vacia(), celdaTexto(texto, estilo), vacia(), vacia()]);
    unirDerecha();
  };
  const empujarTodo = (texto, estilo) => {
    filas.push([celdaTexto(texto, estilo), vacia(), vacia(), vacia()]);
    unirTodo();
  };

  // ── Encabezado: logo en A1, datos de la empresa a su derecha ──────────────
  empujarDerecha(nombreEmpresa(empresa), tituloEmpresaStyle);
  empujarDerecha("R.F.C: " + (empresa.rfc || ""), datosEmpresaStyle);
  empujarDerecha(direccionEmpresa(empresa), datosEmpresaStyle);
  empujarDerecha(contactoEmpresa(empresa), metaStyle);

  filas.push(filaVacia());

  empujarTodo("REPORTE DE CUENTAS POR COBRAR - TOTALES POR CLIENTE", tituloReporteStyle);
  empujarTodo("Fecha de Impresion: " + moment().locale("es").format("LL"), metaStyle);

  filas.push(filaVacia());

  // ── Un bloque por moneda ──────────────────────────────────────────────────
  const monedas = Object.keys(porMoneda).sort((a, b) => a.localeCompare(b));

  if (monedas.length === 0) {
    empujarTodo("No hay registros para los filtros seleccionados.", datosEmpresaStyle);
  }

  monedas.forEach((moneda, indice) => {
    if (indice > 0) filas.push(filaVacia());

    empujarTodo("Moneda: " + moneda, subtituloMonedaStyle);
    filas.push(COLUMNAS.map((columna) => celdaTexto(columna.titulo, headerStyle)));

    let granTotal = 0;
    let granTotalVencido = 0;
    let granTotalNoVencido = 0;

    Object.entries(porMoneda[moneda])
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([cliente, totales]) => {
        granTotal += totales.total;
        granTotalVencido += totales.vencido;
        granTotalNoVencido += totales.noVencido;
        filas.push([
          celdaTexto(cliente),
          celdaMoneda(totales.total),
          celdaMoneda(totales.vencido),
          celdaMoneda(totales.noVencido),
        ]);
      });

    filas.push([
      celdaTexto("TOTAL " + moneda + ":", totalDestacadoTextoStyle),
      celdaMoneda(granTotal, totalDestacadoMoneyStyle),
      celdaMoneda(granTotalVencido, totalDestacadoMoneyStyle),
      celdaMoneda(granTotalNoVencido, totalDestacadoMoneyStyle),
    ]);
  });

  // ── Escritura ─────────────────────────────────────────────────────────────
  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  hoja["!cols"] = COLUMNAS.map((columna) => ({ wch: columna.ancho }));
  hoja["!merges"] = merges;
  hoja["!rows"] = Array.from({ length: FILAS_ENCABEZADO }, () => ({ hpt: ALTO_ENCABEZADO }));
  // Sin `!autofilter`: la hoja son varios bloques y el filtro se tragaría las
  // filas de total. Sin `!freeze` / `!panes`: xlsx-js-style los ignora.

  const libro = XLSXStyle.utils.book_new();
  // Una sola hoja: `inyectarLogoEnExcel` solo parcha `sheet1`.
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Totales CxC");

  const buffer = XLSXStyle.write(libro, { bookType: "xlsx", type: "array" });

  // El logo se inyecta a mano en el zip del .xlsx; si no se puede (sin logo, o
  // en un formato que Excel no acepta como Default), se baja igual sin imagen.
  const conLogo = await inyectarLogoEnExcel(buffer, empresa.logo, { fila: 0, columna: 0 });
  if (conLogo) return conLogo;

  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export default construirExcelCuentasPorCobrarTotales;
